import { nextTick, onBeforeUnmount, ref, type Ref } from 'vue';
import { TOOLTIP_OPEN_DELAY_MS } from '@/components/ui/tooltip/constants';
import { isKeyboardNavigationFocus } from '@/components/ui/tooltip/focusOrigin';

/**
 * The one lazy path for tooltips that are mounted on first use.
 *
 * A results table or a long card list can carry hundreds of tooltips, and
 * mounting the reka-ui Tooltip stack for every one of them froze the page. So
 * the lazy components render only their trigger content at first. The first
 * time the pointer or the keyboard reaches that content, they swap in the real
 * Tooltip. This composable owns that swap for every lazy tooltip:
 * LazyTooltip, OverflowTooltip (and so OverflowText), and the table cells that
 * cannot use either component.
 *
 * Swapping the element out from under reka has three ways to go wrong, and
 * this composable guards against all three.
 *
 *  1. The pointer has already left. reka closes a tooltip on a `pointerleave`
 *     fired by the mounted trigger. The swapped-in element never saw the
 *     pointer arrive, so if the pointer has moved on, nothing ever closes it.
 *     A fast sweep across a list left several tooltips floating over the page.
 *     So the composable watches where the pointer goes after activation, and a
 *     pointer seen anywhere outside the trigger cancels the pending open and
 *     closes the tooltip. The watch ends the first time that happens. After
 *     that, the mounted trigger gets its own leave events and reka manages it.
 *  2. The tooltip opened too soon. The old code forced the tooltip open on the
 *     next tick, so a lazy tooltip skipped the open delay every other tooltip
 *     waits. Now activation only arms a timer of the normal length.
 *  3. Focus is lost. The swap destroys the trigger's node. When that node had
 *     focus, focus fell to `<body>`. The new trigger is re-focused after the
 *     swap, whether the swap came from focus or from the pointer. Focus only
 *     activates the tooltip when the user moved it with the keyboard, by the
 *     same rule `Tooltip` uses (see `focusOrigin.ts`).
 *
 *     Chrome also fires `focusout` on a focused node as it is removed. That is
 *     the swap, not the user leaving, so a `focusout` from the old node after
 *     activation is ignored. Before, it cancelled the open: a button that held
 *     focus, such as one a closing menu had just focused, showed no tooltip on
 *     its first hover.
 *
 * Wiring, on the element shown before activation:
 *   `@pointerover="onProbePointerOver" @pointerout="onProbePointerOut"`
 *   `@focusin="onProbeFocusIn" @focusout="onProbeFocusOut"`
 * and on the trigger element after activation:
 *   `@pointerout="onTriggerLeave" @focusin="onTriggerFocusIn" @focusout="onTriggerLeave"`
 * `pointerover` rather than `pointerenter`, because a `display: contents`
 * wrapper has no box and only ever sees events that bubble up to it.
 */
export interface UseLazyTooltipOptions {
    /**
     * Opens the tooltip. Called once the open delay has passed with the
     * pointer still on the trigger, or straight away for keyboard focus.
     */
    open: () => void;
    /** Closes the tooltip, when the pointer or focus is seen leaving. */
    close: () => void;
    /**
     * The trigger element after activation. The pointer counts as still on the
     * trigger while it is over this element or anything inside it.
     */
    triggerElement: () => HTMLElement | null;
    /**
     * Whether this tooltip may activate at all. Read on every attempt. An
     * overflow tooltip passes a check that the text is actually cut off.
     */
    canActivate?: (probeElement: HTMLElement | null) => boolean;
    /** How long the pointer must rest before the tooltip opens. */
    openDelayMs?: number;
}

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]';

export function useLazyTooltip(options: UseLazyTooltipOptions) {
    const openDelayMs = options.openDelayMs ?? TOOLTIP_OPEN_DELAY_MS;
    const activated = ref(false);
    const probeRef: Ref<HTMLElement | null> = ref(null);

    /**
     * Does the user still want this tooltip by the time it would open? Set
     * when the pointer or focus arrives, cleared the moment either is seen
     * leaving. Every deferred open checks it first.
     */
    let openIntent = false;
    let openTimer: ReturnType<typeof setTimeout> | null = null;
    let watchingPointer = false;

    function clearOpenTimer(): void {
        if (openTimer !== null) {
            clearTimeout(openTimer);
            openTimer = null;
        }
    }

    function isInsideTrigger(target: EventTarget | null): boolean {
        if (!(target instanceof Node)) {
            return false;
        }
        const triggerElement = options.triggerElement();
        const probeElement = probeRef.value;
        return Boolean(triggerElement?.contains(target) || probeElement?.contains(target));
    }

    /**
     * Runs on every `pointerover` in the document while the first activation
     * is in progress. The swapped-in trigger may never receive a
     * `pointerleave`, so the document is the only place guaranteed to see the
     * pointer arrive somewhere else.
     */
    function onDocumentPointerOver(event: Event): void {
        if (!isInsideTrigger(event.target)) {
            releaseIntent();
        }
    }

    /** `pointerout` with no next element: the pointer left the window. */
    function onDocumentPointerOut(event: Event): void {
        if ((event as PointerEvent).relatedTarget === null) {
            releaseIntent();
        }
    }

    function startWatchingPointer(): void {
        if (watchingPointer || typeof document === 'undefined') {
            return;
        }
        watchingPointer = true;
        document.addEventListener('pointerover', onDocumentPointerOver, true);
        document.addEventListener('pointerout', onDocumentPointerOut, true);
    }

    function stopWatchingPointer(): void {
        if (!watchingPointer) {
            return;
        }
        watchingPointer = false;
        document.removeEventListener('pointerover', onDocumentPointerOver, true);
        document.removeEventListener('pointerout', onDocumentPointerOut, true);
    }

    /** The pointer or focus has left. Cancel any pending open and close. */
    function releaseIntent(): void {
        openIntent = false;
        clearOpenTimer();
        stopWatchingPointer();
        options.close();
    }

    function isAllowed(): boolean {
        return options.canActivate ? options.canActivate(probeRef.value) : true;
    }

    function focusedElementInsideProbe(): boolean {
        const focusedElement = typeof document === 'undefined' ? null : document.activeElement;
        return Boolean(focusedElement && probeRef.value?.contains(focusedElement));
    }

    /** Puts focus on the swapped-in trigger, where the user had it on the old one. */
    function refocusTrigger(): void {
        const triggerElement = options.triggerElement();
        const focusable = triggerElement?.matches?.(FOCUSABLE_SELECTOR)
            ? triggerElement
            : triggerElement?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        focusable?.focus();
    }

    function onProbePointerOver(): void {
        if (activated.value || !isAllowed()) {
            return;
        }
        if (focusedElementInsideProbe()) {
            void nextTick(refocusTrigger);
        }
        openIntent = true;
        activated.value = true;
        startWatchingPointer();
        clearOpenTimer();
        openTimer = setTimeout(() => {
            openTimer = null;
            if (openIntent) {
                options.open();
            }
        }, openDelayMs);
    }

    function onProbeFocusIn(event: FocusEvent): void {
        if (activated.value || !isKeyboardNavigationFocus(event) || !isAllowed()) {
            return;
        }
        openIntent = true;
        activated.value = true;
        void nextTick(() => {
            if (!openIntent) {
                return;
            }
            // The swap replaced the focused element. Put focus back where the
            // user had it rather than dropping them to <body>.
            refocusTrigger();
            options.open();
        });
    }

    /**
     * `pointerout` and `focusout` bubble, so they also fire when the pointer or
     * focus moves from one element inside the trigger to another, such as from
     * one count in a row of counts to the next. That is not leaving.
     */
    function leave(event: PointerEvent | FocusEvent): void {
        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && isInsideTrigger(nextTarget)) {
            return;
        }
        releaseIntent();
    }

    /** A `focusout` from the old node once the swap has begun is the swap removing it. */
    function onProbeFocusOut(event: FocusEvent): void {
        if (activated.value) {
            return;
        }
        leave(event);
    }

    /**
     * Keyboard focus arriving on the trigger after the swap. reka listens for
     * `focus` on the trigger wrapper, but `focus` does not bubble and the
     * wrapper has no box, so reka never hears it. Without this, a tooltip that
     * had shown once never opened again from the keyboard.
     */
    function onTriggerFocusIn(event: FocusEvent): void {
        if (!isKeyboardNavigationFocus(event)) {
            return;
        }
        openIntent = true;
        options.open();
    }

    onBeforeUnmount(() => {
        openIntent = false;
        clearOpenTimer();
        stopWatchingPointer();
    });

    return {
        activated,
        probeRef,
        onProbePointerOver,
        onProbePointerOut: leave,
        onProbeFocusIn,
        onProbeFocusOut,
        onTriggerFocusIn,
        onTriggerLeave: leave,
    };
}
